import { useEffect, useRef, useState } from 'react';
import { X, Check, Plus, RefreshCw, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import { gastosApi } from '../../utils/api';
import ConfirmModal from '../ConfirmModal';

/**
 * Configuración → Facturas recibidas: qué debe traer una factura para aceptarla y qué hace el sistema
 * si no lo trae. Una línea por opción; cada una se edita en un modal corto de dos columnas.
 */
const PASOS = [
  { campo: 'modo_incumple', envio: 'modoIncumple', plantilla: 'solicitud', t: 'Factura que incumple una regla que bloquea',
    auto: 'Pasa sola a «Pendientes de rechazo» y sale el correo al proveedor.',
    man: 'Queda marcada en «Por Confirmar»; alguien decide pasarla y enviar el correo.' },
  { campo: 'modo_nc', envio: 'modoNc', t: 'Llega la nota de crédito que anula una factura pendiente de rechazo',
    auto: 'Si la NC referencia esa factura y la anula al 100 %, se aceptan las dos (original y NC). La factura nueva sigue su camino normal en «Por Confirmar».',
    man: 'Queda lista para que alguien acepte las dos (original y NC).' },
  { campo: 'modo_vence', envio: 'modoVence', plantilla: 'rechazo', t: 'Se vence el plazo de Hacienda sin corrección del proveedor',
    auto: 'Se rechaza sola ante Hacienda el último día hábil, con el motivo de la regla.',
    man: 'Se avisa y alguien hace clic en «Rechazar ante Hacienda».' },
];
const NIVELES = [
  { v: 'bloquea', t: 'Bloquea', d: 'No se acepta', cl: 'border-red-400 text-red-700' },
  { v: 'advierte', t: 'Advierte', d: 'Se ve, se acepta', cl: 'border-amber-400 text-amber-700' },
  { v: 'apagada', t: 'Apagada', d: 'No se revisa', cl: 'border-gray-300 text-gray-500' },
];
// Líneas sin categorizar al confirmar: Hacienda no presume ninguna condición del IVA (ni al aceptar ni
// por plazo vencido); lo decide la empresa
const SIN_CATEGORIZAR = { campo: 'sin_categorizar', envio: 'sinCategorizar', t: 'Facturas con líneas sin categorizar',
  nota: 'Al confirmar una factura, cada línea debe tener su categoría del IVA. Si alguna queda sin categorizar:',
  opciones: [
    ['bloquea', 'Bloquear', 'No se puede confirmar hasta categorizar todas sus líneas.'],
    ['credito_total', 'Confirmar con crédito total', 'Se confirma igual y esas líneas se toman con todo su IVA como crédito (condición 01 ante Hacienda).'],
  ] };
const fechaCorta = (d) => d ? String(d).slice(0, 10).split('-').reverse().join('/') : 'Siempre';

function Modal({ titulo, onClose, children, pie, ancho = 'max-w-3xl' }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className={`bg-white rounded-lg w-full ${ancho} border-2 border-gray-200`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">{titulo}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        {children}
        <div className="flex justify-end gap-3 px-6 py-4 border-t">{pie}</div>
      </div>
    </div>
  );
}

/** Buscador de los datos de la factura (en español), en lugar de escribir el nombre técnico del XML */
function BuscadorCampo({ campos, valor, onElegir }) {
  const [q, setQ] = useState('');
  const elegido = campos.find(c => c.ruta === valor);
  const norm = (t) => String(t || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const lista = campos.filter(c => !q || norm(c.nombre + ' ' + c.descripcion).includes(norm(q)));
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Dato que debe traer la factura</label>
      {elegido && (
        <div className="mb-2 px-3 py-2 border-2 border-indigo-400 rounded-lg text-sm text-indigo-700">
          {elegido.nombre}
          <div className="text-[11px] text-gray-500 font-mono">{elegido.ruta}</div>
        </div>
      )}
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar: correo, actividad, plazo, medio de pago..."
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      <div className="mt-1 border rounded-lg max-h-40 overflow-y-auto divide-y">
        {lista.map(c => (
          <button key={c.ruta} type="button" onClick={() => onElegir(c)}
            className={`w-full text-left px-3 py-1.5 hover:bg-gray-50 border-l-2 ${c.ruta === valor ? 'border-indigo-500 text-indigo-700' : 'border-transparent'}`}>
            <div className="text-sm text-gray-900">{c.nombre}</div>
            {c.descripcion && <div className="text-[11px] text-gray-500 truncate">{c.descripcion}</div>}
          </button>
        ))}
        {lista.length === 0 && <div className="px-3 py-2 text-sm text-gray-500">Ningún dato coincide</div>}
      </div>
      <p className="text-xs text-gray-500 mt-1">Son los datos que hoy son opcionales en la factura electrónica 4.4 (los obligatorios ya los revisa Hacienda).</p>
    </div>
  );
}

/** Plantilla del correo al proveedor: texto con variables a la izquierda, el correo tal como sale a la derecha */
function PlantillaCorreo({ tipo, emisorId, onClose }) {
  const [p, setP] = useState(null); // { asunto, cuerpo, porDefecto, variables }
  const [vista, setVista] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const textoRef = useRef(null);
  useEffect(() => {
    gastosApi.getPlantillaRecepcion(tipo, emisorId).then(r => setP(r.data?.data))
      .catch(err => { (!err.response && toast.error('No se pudo cargar la plantilla')); onClose(); });
  }, [tipo, emisorId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!p) return undefined;
    const t = setTimeout(() => {
      gastosApi.vistaPreviaPlantillaRecepcion(tipo, { asunto: p.asunto, cuerpo: p.cuerpo }, emisorId)
        .then(r => setVista(r.data?.data)).catch(() => {});
    }, vista ? 400 : 0); // la primera vez, de una
    return () => clearTimeout(t);
  }, [p?.asunto, p?.cuerpo, tipo, emisorId]); // eslint-disable-line react-hooks/exhaustive-deps
  const insertar = (v) => {
    const el = textoRef.current;
    if (!el) return setP({ ...p, cuerpo: `${p.cuerpo}${v}` });
    const ini = el.selectionStart ?? p.cuerpo.length, fin = el.selectionEnd ?? ini;
    setP({ ...p, cuerpo: p.cuerpo.slice(0, ini) + v + p.cuerpo.slice(fin) });
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = ini + v.length; }, 0);
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-6xl border-2 border-gray-200 max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">{tipo === 'rechazo' ? 'Correo de aviso de rechazo ante Hacienda' : 'Correo al proveedor: factura no aceptada'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        {!p ? <div className="py-10 text-center text-sm text-gray-500">Cargando...</div> : (
          <div className="grid grid-cols-1 lg:grid-cols-2 min-h-0 flex-1">
            <div className="p-5 space-y-3 border-b lg:border-b-0 lg:border-r overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Asunto</label>
                <input value={p.asunto} onChange={e => setP({ ...p, asunto: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Texto</label>
                <textarea ref={textoRef} rows={12} value={p.cuerpo} onChange={e => setP({ ...p, cuerpo: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm leading-relaxed" />
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Datos de la factura (clic para insertar):</div>
                <div className="flex flex-wrap gap-1.5">
                  {p.variables.map(x => (
                    <button key={x.v} type="button" onClick={() => insertar(x.v)} title={x.d}
                      className="px-2 py-0.5 rounded-full border border-indigo-300 text-indigo-700 text-xs hover:bg-indigo-50">{x.d}</button>
                  ))}
                </div>
              </div>
              <button type="button" onClick={() => setP({ ...p, asunto: p.porDefecto.asunto, cuerpo: p.porDefecto.cuerpo })}
                className="text-xs text-gray-500 hover:text-gray-700 underline">Volver al texto original</button>
            </div>
            <div className="p-5 flex flex-col min-h-0 bg-gray-50">
              <div className="text-xs text-gray-500 mb-1">Así lo recibe el proveedor (con datos de ejemplo)</div>
              <div className="text-sm mb-2"><span className="text-gray-500">Asunto:</span> <span className="font-medium">{vista?.asunto || ''}</span></div>
              {vista?.html
                ? <iframe key={vista.html.length + (vista.asunto || '')} title="Vista previa" sandbox="" srcDoc={vista.html} className="w-full flex-1 min-h-[420px] border rounded-lg bg-white" />
                : <div className="w-full flex-1 min-h-[420px] border rounded-lg bg-white flex items-center justify-center text-sm text-gray-400">Armando la vista previa...</div>}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50">Cancelar</button>
          <button disabled={!p || guardando} onClick={async () => {
            setGuardando(true);
            try { await gastosApi.guardarPlantillaRecepcion(tipo, { asunto: p.asunto, cuerpo: p.cuerpo }, emisorId); toast.success('Plantilla guardada'); onClose(); }
            catch (err) { (!err.response && toast.error('No se pudo guardar')); }
            finally { setGuardando(false); }
          }} className="flex items-center gap-2 px-4 py-2 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50"><Check size={18} /> Guardar</button>
        </div>
      </div>
    </div>
  );
}

export default function RecepcionFacturas({ emisorId }) {
  const [data, setData] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [paso, setPaso] = useState(null);          // { ...PASO, valor }
  const [regla, setRegla] = useState(null);        // regla en edición (o { nueva: true })
  const [aviso, setAviso] = useState(null);        // { dias, copia }
  const [plantilla, setPlantilla] = useState(null); // 'solicitud' | 'rechazo'
  const [devueltas, setDevueltas] = useState(null); // resumen: facturas que volvieron a «Por Confirmar»
  const [campos, setCampos] = useState([]);         // datos opcionales de la FE v4.4 que se pueden exigir
  useEffect(() => { gastosApi.getCamposFE44().then(r => setCampos(r.data?.data || [])).catch(() => {}); }, []);

  const [quitar, setQuitar] = useState(null);     // regla a quitar (con confirmación)
  const [errorCarga, setErrorCarga] = useState(null);
  const cargar = () => gastosApi.getRecepcionConfig(emisorId)
    .then(r => { setData(r.data?.data); setErrorCarga(null); })
    .catch(err => { setErrorCarga(err.response?.data?.error || 'No se pudo cargar la configuración'); if (!err.response) toast.error('No se pudo cargar la configuración'); });
  useEffect(() => { cargar(); }, [emisorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const guardar = async (fn, ok) => {
    setGuardando(true);
    try {
      const r = await fn();
      setData(r.data?.data);
      toast.success(ok);
      // Con las reglas nuevas, las que estaban en pendientes y ya cumplen volvieron solas a «Por Confirmar»
      if (r.data?.devueltas?.length) setDevueltas(r.data.devueltas);
      return true;
    } catch (err) {
      (!err.response && toast.error('No se pudo guardar'));
      return false;
    } finally {
      setGuardando(false);
    }
  };

  if (!data && errorCarga) return <div className="py-10 text-center text-sm text-red-700">{errorCarga} · <button type="button" onClick={cargar} className="underline">Reintentar</button></div>;
  if (!data) return <div className="py-10 text-center text-sm text-gray-500 flex items-center justify-center gap-2"><RefreshCw size={16} className="animate-spin" /> Cargando...</div>;
  const { config, reglas } = data;
  const nivel = (v) => NIVELES.find(n => n.v === v) || NIVELES[1];

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">Qué debe traer una factura de proveedor para aceptarla y qué hace el sistema si no lo trae. Aplica solo a esta empresa.</p>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Qué hace el sistema solo</div>
          <div className="border rounded-lg divide-y">
            {PASOS.map(p => (
              <button key={p.campo} type="button" onClick={() => setPaso({ ...p, valor: config[p.campo] })}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0 truncate">{p.t}</span>
                <span className={`px-2 py-0.5 rounded-full border text-xs whitespace-nowrap ${config[p.campo] === 'auto' ? 'border-indigo-400 text-indigo-700' : 'border-gray-300 text-gray-600'}`}>
                  {config[p.campo] === 'auto' ? 'Automático' : 'Manual'}
                </span>
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setPaso({ ...SIN_CATEGORIZAR, valor: config.sin_categorizar || 'bloquea' })}
            className="w-full text-left border rounded-lg px-4 py-2.5 hover:bg-gray-50 flex items-center justify-between gap-3 text-sm">
            <span className="min-w-0 truncate">{SIN_CATEGORIZAR.t}</span>
            <span className={`px-2 py-0.5 rounded-full border text-xs whitespace-nowrap ${(config.sin_categorizar || 'bloquea') === 'bloquea' ? 'border-red-400 text-red-700' : 'border-gray-300 text-gray-600'}`}>
              {(config.sin_categorizar || 'bloquea') === 'bloquea' ? 'Bloquear' : 'Crédito total'}
            </span>
          </button>
          <button type="button" onClick={() => setAviso({ dias: config.dias_aviso, copia: config.copia_interna || '' })}
            className="w-full text-left border rounded-lg px-4 py-2.5 hover:bg-gray-50 flex items-center justify-between gap-3 text-sm">
            <span>Aviso de vencimiento y copia interna</span>
            <span className="text-gray-500 whitespace-nowrap truncate">{config.dias_aviso} días hábiles{config.copia_interna ? ` · ${config.copia_interna}` : ''}</span>
          </button>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Reglas</div>
            <button type="button" onClick={() => setRegla({ nueva: true, nivel: 'advierte', rige_desde: '', texto_proveedor: '', campo_xml: '' })}
              className="flex items-center gap-1 px-3 py-1 border-2 border-indigo-400 text-indigo-600 rounded-lg text-xs hover:bg-indigo-50"><Plus size={14} /> Dato obligatorio</button>
          </div>
          <div className="border rounded-lg divide-y">
            {reglas.map(r => (
              <button key={r.id} type="button" onClick={() => setRegla({ ...r, rige_desde: r.rige_desde ? String(r.rige_desde).slice(0, 10) : '' })}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0 truncate">{r.nombre}</span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 hidden sm:inline">{fechaCorta(r.rige_desde)}</span>
                  {r.nivel_si_falta && <span className="text-xs text-gray-500 hidden md:inline whitespace-nowrap">si no la trae: {nivel(r.nivel_si_falta).t.toLowerCase()}</span>}
                  <span className={`px-2 py-0.5 rounded-full border text-xs ${nivel(r.nivel).cl}`}>{nivel(r.nivel).t}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {paso && (
        <Modal titulo={paso.t} onClose={() => setPaso(null)} pie={<>
          {paso.plantilla && (
            <button type="button" onClick={() => setPlantilla(paso.plantilla)}
              className="mr-auto flex items-center gap-2 px-4 py-2 border-2 border-indigo-300 text-indigo-600 rounded-lg hover:bg-indigo-50">
              <Settings size={16} /> Plantilla del correo
            </button>
          )}
          <button onClick={() => setPaso(null)} className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50">Cancelar</button>
          <button disabled={guardando} onClick={async () => { if (await guardar(() => gastosApi.updateRecepcionConfig({ [paso.envio]: paso.valor }, emisorId), 'Guardado')) setPaso(null); }}
            className="flex items-center gap-2 px-4 py-2 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50"><Check size={18} /> Guardar</button>
        </>}>
          {paso.nota && <p className="px-6 pt-5 text-sm text-gray-600">{paso.nota}</p>}
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {(paso.opciones || [['auto', 'Automático', paso.auto], ['manual', 'Manual', paso.man]]).map(([v, t, d]) => (
              <button key={v} type="button" onClick={() => setPaso({ ...paso, valor: v })}
                className={`text-left p-4 rounded-lg border-2 ${paso.valor === v ? 'border-indigo-500' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className={`font-semibold text-sm ${paso.valor === v ? 'text-indigo-700' : 'text-gray-900'}`}>{t}</div>
                <div className="text-xs text-gray-600 mt-1">{d}</div>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {regla && (
        <Modal titulo={regla.nueva ? 'Nuevo dato obligatorio en la factura' : regla.nombre} ancho="max-w-4xl" onClose={() => setRegla(null)} pie={<>
          {!regla.nueva && regla.tipo === 'campo_xml' && (
            <button disabled={guardando} onClick={() => setQuitar(regla)}
              className="mr-auto px-4 py-2 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50">Quitar</button>
          )}
          <button onClick={() => setRegla(null)} className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50">Cancelar</button>
          <button disabled={guardando} onClick={async () => {
            const datos = { nivel: regla.nivel, ...(regla.nivel_si_falta ? { nivelSiFalta: regla.nivel_si_falta } : {}), rigeDesde: regla.rige_desde || '', textoProveedor: regla.texto_proveedor, ...(regla.nueva || regla.tipo === 'campo_xml' ? { campoXml: regla.campo_xml } : {}) };
            const ok = await guardar(() => regla.nueva ? gastosApi.crearRecepcionRegla(datos, emisorId) : gastosApi.updateRecepcionRegla(regla.id, datos, emisorId), 'Regla guardada');
            if (ok) setRegla(null);
          }} className="flex items-center gap-2 px-4 py-2 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50"><Check size={18} /> Guardar</button>
        </>}>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              {(regla.nueva || regla.tipo === 'campo_xml') && (
                <BuscadorCampo campos={campos} valor={regla.campo_xml} onElegir={(c) => setRegla({
                  ...regla, campo_xml: c.ruta,
                  texto_proveedor: regla.texto_proveedor || `La factura no trae: ${c.nombre.toLowerCase()}.`
                })} />
              )}
              {[
                ['nivel', regla.nivel_si_falta ? 'Si trae una que no es de la empresa' : 'Nivel'],
                ...(regla.nivel_si_falta ? [['nivel_si_falta', 'Si no la trae']] : [])
              ].map(([campo, titulo]) => (
                <div key={campo}>
                  <div className="text-sm font-medium text-gray-700 mb-2">{titulo}</div>
                  <div className="grid grid-cols-3 gap-2">
                    {NIVELES.map(n => (
                      <button key={n.v} type="button" onClick={() => setRegla({ ...regla, [campo]: n.v })}
                        className={`p-2 rounded-lg border-2 text-center ${regla[campo] === n.v ? n.cl : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                        <div className="text-sm font-medium">{n.t}</div><div className="text-xs text-gray-500">{n.d}</div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rige desde</label>
                <input type="date" value={regla.rige_desde || ''} onChange={e => setRegla({ ...regla, rige_desde: e.target.value })}
                  className="w-48 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <p className="text-xs text-gray-500 mt-1">Vacío: aplica a todas las facturas.</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Texto para el proveedor</label>
              <textarea rows={6} value={regla.texto_proveedor || ''} onChange={e => setRegla({ ...regla, texto_proveedor: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <p className="text-xs text-gray-500 mt-1">Va en el correo al proveedor cuando la factura incumple esta regla.</p>
            </div>
          </div>
        </Modal>
      )}

      {devueltas && (
        <Modal titulo="Facturas que volvieron a «Por Confirmar»" ancho="max-w-5xl" onClose={() => setDevueltas(null)} pie={
          <button onClick={() => setDevueltas(null)} className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50">Cerrar</button>
        }>
          <div className="p-6 space-y-3">
            <p className="text-sm text-gray-600">Con las reglas actuales ya cumplen: salieron de «Pendientes de rechazo» y se confirman como cualquier otra.</p>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                    <th className="px-3 py-2">Fecha</th><th className="px-3 py-2">Proveedor</th><th className="px-3 py-2">No. Documento</th>
                    <th className="px-3 py-2">Estaba por</th><th className="px-3 py-2">Ahora</th><th className="px-3 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {devueltas.map(d => (
                    <tr key={d.id}>
                      <td className="px-3 py-2 whitespace-nowrap">{fechaCorta(d.fecha)}</td>
                      <td className="px-3 py-2"><div className="max-w-[14rem] truncate" title={d.proveedor}>{d.proveedor}</div></td>
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{d.numero}</td>
                      <td className="px-3 py-2"><div className="max-w-[14rem] truncate text-red-600" title={d.antes.join(' · ')}>{d.antes.join(' · ')}</div></td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-full border text-xs ${d.ahora === 'advierte' ? 'border-amber-400 text-amber-700' : 'border-green-500 text-green-700'}`}>{d.ahora === 'advierte' ? 'Advierte' : 'Cumple'}</span>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap font-medium">{Number(d.total).toLocaleString('es-CR', { style: 'currency', currency: d.moneda || 'CRC' })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Modal>
      )}

      <ConfirmModal isOpen={!!quitar} onClose={() => setQuitar(null)}
        onConfirm={async () => { const r = quitar; setQuitar(null); if (await guardar(() => gastosApi.borrarRecepcionRegla(r.id, emisorId), 'Regla quitada')) setRegla(null); }}
        title="Quitar regla" message={quitar ? `Se quita «${quitar.nombre}». Las facturas en «Pendientes de rechazo» que ya no incumplan nada vuelven solas a «Por Confirmar».` : ''}
        confirmText="Quitar" variant="danger" />

      {plantilla && <PlantillaCorreo tipo={plantilla} emisorId={emisorId} onClose={() => setPlantilla(null)} />}

      {aviso && (
        <Modal titulo="Aviso de vencimiento y copia interna" onClose={() => setAviso(null)} pie={<>
          <button onClick={() => setAviso(null)} className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50">Cancelar</button>
          <button disabled={guardando} onClick={async () => { if (await guardar(() => gastosApi.updateRecepcionConfig({ diasAviso: aviso.dias, copiaInterna: aviso.copia }, emisorId), 'Guardado')) setAviso(null); }}
            className="flex items-center gap-2 px-4 py-2 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50"><Check size={18} /> Guardar</button>
        </>}>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Avisar antes del vencimiento</label>
              <div className="flex items-center gap-2">
                <input type="number" min="0" max="8" value={aviso.dias} onChange={e => setAviso({ ...aviso, dias: e.target.value })} className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <span className="text-sm text-gray-600">días hábiles</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">El plazo de Hacienda: 8 días hábiles desde el primer día del mes siguiente al de la factura.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Copia interna de los correos</label>
              <input value={aviso.copia} onChange={e => setAviso({ ...aviso, copia: e.target.value })} placeholder="cuentasporpagar@empresa.cr"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <p className="text-xs text-gray-500 mt-1">Los destinatarios del proveedor salen de sus contactos registrados.</p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
